import {
  ToothFullHeadMPRSlice,
  ToothFullHeadMPRSliceSelection,
} from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';

import { makeAnnotations } from '@/entities/assets';

import {
  makeSavedFullHeadMprView,
  resolveFullHeadMprSeriesContext,
} from '@/features/MPR';

import {
  FULL_HEAD_MPR_PROJECTION_TO_SERIES,
  ORIENTATION_TO_SERIES,
  type FullHeadMprSliceRecordView,
  type FullHeadMprSliceRecordViewsByTileID,
  type FullHeadMprSliceViewOptionsView,
} from '../config';

import { hasVisibleFullHeadMprSliceViewOptions } from './applyFullHeadMprSliceViewOptions';
import { makeFullHeadMprSliceTileID } from './fullHeadMprSliceTileID';

export const makeFullHeadMprSliceViewOptions = (
  slice: ToothFullHeadMPRSlice,
): FullHeadMprSliceViewOptionsView => ({
  brightness: slice.MedicalImageFeatures?.ViewOptionsV2?.Brightness ?? 0,
  contrast: slice.MedicalImageFeatures?.ViewOptionsV2?.Contrast ?? 0,
  invert: slice.MedicalImageFeatures?.ViewOptionsV2?.Invert ?? false,
  sharpness: slice.MedicalImageFeatures?.ViewOptionsV2?.Sharpness ?? 0,
});

const resolveSelectionOverride = (
  selection: ToothFullHeadMPRSliceSelection,
): boolean | null => {
  if (
    selection ===
    ToothFullHeadMPRSliceSelection.ToothFullHeadMPRSliceSelectionAdded
  ) {
    return true;
  }

  if (
    selection ===
    ToothFullHeadMPRSliceSelection.ToothFullHeadMPRSliceSelectionRemoved
  ) {
    return false;
  }

  return null;
};

const makeFullHeadMprSliceRecordView = ({
  slice,
  toothID,
}: {
  slice: ToothFullHeadMPRSlice;
  toothID: string;
}): FullHeadMprSliceRecordView | null => {
  const source = slice.Source?.case === 'View' ? slice.Source.value : null;
  const series = slice.Source?.case === 'Series' ? slice.Source.value : slice;
  const view = source ? makeSavedFullHeadMprView(source) : null;
  const seriesName = view
    ? FULL_HEAD_MPR_PROJECTION_TO_SERIES[view.projection]
    : ORIENTATION_TO_SERIES[series.Orientation];

  if (!seriesName || (source && !view)) {
    return null;
  }

  const layers =
    slice.MedicalImageFeatures?.Annotations?.Layers.filter(
      (layer) => !layer.Readonly,
    ) ?? [];
  const annotations = makeAnnotations(layers);
  const viewOptions = makeFullHeadMprSliceViewOptions(slice);
  const seriesContext = resolveFullHeadMprSeriesContext({ slice });
  const annotationView = slice.EditorState?.AnnotationView
    ? makeSavedFullHeadMprView(slice.EditorState.AnnotationView, seriesContext)
    : null;
  const displayView = slice.EditorState?.DisplayView
    ? makeSavedFullHeadMprView(slice.EditorState.DisplayView, seriesContext)
    : null;

  return {
    source: view ? { kind: 'view', view } : { kind: 'series' },
    annotations,
    geometry: slice.Geometry
      ? {
          geometrySource: slice.Geometry.GeometrySource,
          heightPx: slice.Geometry.HeightPx,
          scaleMmPerPx: slice.Geometry.Scale,
          thicknessMm: slice.Geometry.ThicknessMm,
          volumeFileID: slice.Geometry.VolumeFileID,
          widthPx: slice.Geometry.WidthPx,
        }
      : null,
    hasAnnotations: annotations.length > 0,
    hasViewOptions: hasVisibleFullHeadMprSliceViewOptions(viewOptions),
    layers,
    offsetMm: source ? 0 : series.OffsetMm,
    recordID: slice.ID,
    selectionOverride: resolveSelectionOverride(slice.Selection),
    seriesName,
    tileID: source
      ? `view:${slice.ID}`
      : makeFullHeadMprSliceTileID({
          offsetMm: series.OffsetMm,
          seriesName,
          sliceStepMm:
            slice.Source?.case === 'Series'
              ? slice.Source.value.SliceStepMm || undefined
              : undefined,
          toothID,
        }),
    viewOptions,
    ...(annotationView && displayView
      ? { editorState: { annotationView, displayView } }
      : {}),
  };
};

export const makeFullHeadMprSliceRecordViews = ({
  slices,
  toothID,
}: {
  slices: ToothFullHeadMPRSlice[];
  toothID: string;
}): FullHeadMprSliceRecordViewsByTileID =>
  slices.reduce<FullHeadMprSliceRecordViewsByTileID>((views, slice) => {
    const view = makeFullHeadMprSliceRecordView({ slice, toothID });

    return view ? { ...views, [view.tileID]: view } : views;
  }, {});
